import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {SpecialZoomLevel, Viewer, Worker} from "@react-pdf-viewer/core";
import {defaultLayoutPlugin} from "@react-pdf-viewer/default-layout";
import {pageNavigationPlugin} from "@react-pdf-viewer/page-navigation";
import {zoomPlugin} from "@react-pdf-viewer/zoom";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "./PdfViewer.css";

/**
 * The pdf.js worker, bundled by webpack straight out of node_modules so it is always the
 * same version as the pdfjs-dist the viewer runs - a copy under public/ has to be kept in
 * step by hand, and a missing one 404s (or gets index.html) and no document ever loads.
 */
const PDF_WORKER_URL = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();

/**
 * A PDF pane that draws highlight rectangles over the pages and scrolls to them.
 *
 * Built on @react-pdf-viewer, which the rest of this app already uses. The library owns
 * fetching, parsing, virtualisation, lazy page rendering, zoom, rotation, scroll mode and
 * the whole toolbar; this file owns the highlights and the imperative API around them.
 *
 * Highlights are absolutely-positioned DOM overlays sized in PERCENT of the page layer, not
 * rectangles painted onto the canvas. That is the reason this version is a fraction of the
 * size of the hand-rolled one it replaces: a percentage rectangle follows zoom, re-render
 * and page recycling on its own, so there is no repaint bookkeeping, no "erase a highlight
 * by re-rendering the whole page", and no tracking of which pages have been annotated.
 *
 * The prop contract and the imperative handle are unchanged from the pdfjs-dist version, so
 * callers did not have to move.
 */

const DEFAULT_FILE_NAME = 'claimDocument.pdf';
const MIN_SCALE = 0.2;
const SCALE_STEP = 0.2;
/** How long to keep looking for a page the viewer has not virtualised into the DOM yet. */
const SCROLL_RETRY_LIMIT = 40;
const SCROLL_RETRY_MS = 150;

/**
 * Presigned S3 urls are shown in the browser's own PDF viewer, straight off S3, in every
 * environment - the same url that opens when pasted into the address bar.
 *
 * pdf.js reads a file with XHR, so it needs the bucket to answer with CORS headers, and
 * mighty-docs-abhi-uat has none (no Access-Control-Allow-Origin, preflight 403). An iframe is
 * a navigation, not an XHR, so CORS does not apply. The cost is the highlight overlays, which
 * only pdf.js can draw; the viewer is still sent to the target's page with #page=.
 */
const isS3Url = (url) => String(url).includes('amazonaws');

/** The 1-based page a scroll target points at, for the native viewer's #page= fragment. */
const targetPage = (target) => {
    const first = Array.isArray(target) ? target.find(Boolean) : target;
    if (!first) {
        return null;
    }
    const resolved = transformAnnotationCoordinates(first, 1, 1);
    if (resolved) {
        return Math.max(resolved.pageIndex, 1);
    }
    if (typeof first.page_number === 'number') {
        return Math.max(first.page_number, 1);
    }
    return typeof first.pageIndex === 'number' ? first.pageIndex + 1 : null;
};

/* ------------------------------------------------------------------ *
 * Coordinate transform
 * ------------------------------------------------------------------ */

/**
 * Turn one annotation, in whichever of the three shapes it arrives, into PDF-native
 * (scale = 1) coordinates with the origin at the top-left.
 *
 * The `pageIndex` that comes out is 1-BASED - the first branch adds one - and is used
 * directly as a page number everywhere downstream. Renaming it or dropping the offset
 * desynchronises the page maps.
 *
 * @returns {{x:number,y:number,width:number,height:number,pageIndex:number}|null}
 */
export function transformAnnotationCoordinates(c, canvasWidth, canvasHeight) {
    if (!c) {
        return null;
    }
    const {x, y, width, height, left, top, right, bottom, pageIndex, page_number: pageNumber} = c;

    if ([x, y, width, height].every((v) => typeof v === 'number')) {
        // PDF space has its origin at the bottom-left; the page layer draws from the top.
        return {x, y: canvasHeight - (y + height), width, height, pageIndex: (pageIndex ?? 0) + 1};
    }

    if ([left, top, right, bottom].every((v) => typeof v === 'number')) {
        return {
            x: left * canvasWidth,
            y: top * canvasHeight,
            width: (right - left) * canvasWidth,
            height: (bottom - top) * canvasHeight,
            pageIndex: pageNumber ?? 0,
        };
    }

    if ([left, top, width, height].every((v) => typeof v === 'number')) {
        return {
            x: left * canvasWidth,
            y: top * canvasHeight,
            width: width * canvasWidth,
            height: height * canvasHeight,
            pageIndex: pageNumber ?? 0,
        };
    }

    return null;
}

/** A stable identity for one annotation, so "the one that was clicked" survives a re-render. */
const annotationKey = (c) => [
    c.pageIndex ?? c.page_number ?? 0,
    c.x ?? c.left ?? 0,
    c.y ?? c.top ?? 0,
    c.width ?? c.right ?? 0,
    c.height ?? c.bottom ?? 0,
].join('|');

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const PdfViewer = forwardRef(function PdfViewer(props, ref) {
    const {
        pdfUrl,
        fileName,
        coordinates,
        scrollTarget,
        width,
        height,
        doublePageWidth,
        isRestPdfMode,
        apiBaseUrl,
        getAuthToken,
        onPageChange,
    } = props;

    // Plugin instances are built in the render body, which is how the library documents it
    // and how src/components/PDFViewer.jsx already does it. defaultLayout brings the sidebar
    // (thumbnails, bookmarks, attachments) and the toolbar - search, page navigation, zoom,
    // rotate, print, full screen, properties, download, open - so none of that is hand-rolled.
    const defaultLayoutPluginInstance = defaultLayoutPlugin();
    const pageNavigationPluginInstance = pageNavigationPlugin();
    const zoomPluginInstance = zoomPlugin();

    const [totalPages, setTotalPages] = useState(0);
    const [loadFailed, setLoadFailed] = useState(false);
    /** The page the browser's viewer is on for an S3 document - see isS3Url. */
    const [nativePage, setNativePage] = useState(null);
    /** The annotations drawn in the "current" colour - the ones the caller just scrolled to. */
    const [currentKeys, setCurrentKeys] = useState(() => new Set());

    const rootRef = useRef(null);
    const docRef = useRef(null);
    const scaleRef = useRef(1);
    const retryRef = useRef(null);
    const pendingTargetRef = useRef(null);

    // The plugin functions are new objects on every render; the handle below reads them
    // through this ref so it never closes over a stale one.
    const apiRef = useRef({});
    apiRef.current = {
        jumpToPage: pageNavigationPluginInstance.jumpToPage,
        zoomTo: zoomPluginInstance.zoomTo,
    };

    /* -------------------------------------------------------------- *
     * Where the bytes come from
     * -------------------------------------------------------------- */

    // Read every render: a refreshed login must reach the next request for the same document.
    const authToken = getAuthToken();
    const source = useMemo(() => {
        // A crude sentinel for placeholder and empty urls: nothing this app fetches has a url
        // this short, and the library would otherwise spend a request finding out.
        if (!pdfUrl || String(pdfUrl).length < 25) {
            return null;
        }
        return isS3Url(pdfUrl)
            ? {fileUrl: pdfUrl, httpHeaders: undefined, isS3: true}
            : {
                fileUrl: `${apiBaseUrl || ''}/api/v1/document/fetch/?token=${encodeURIComponent(pdfUrl)}`,
                httpHeaders: {Authorization: `Bearer ${authToken}`},
                isS3: false,
            };
    }, [pdfUrl, apiBaseUrl, authToken]);

    useEffect(() => {
        setLoadFailed(false);
        setNativePage(null);
        setCurrentKeys(new Set());
        docRef.current = null;
    }, [source]);

    const useNativeViewer = Boolean(source && source.isS3);

    /* -------------------------------------------------------------- *
     * Highlights
     * -------------------------------------------------------------- */

    /**
     * The rectangles that belong on one page, in percent of that page.
     *
     * Percent rather than pixels is the whole trick: the page layer is already sized to the
     * current zoom, so the browser rescales the overlays and nothing here has to know the
     * zoom level, watch for it changing, or repaint anything.
     */
    const rectsForPage = useCallback((pageIndex, nativeWidth, nativeHeight) => {
        if (!nativeWidth || !nativeHeight) {
            return [];
        }
        const rects = [];
        // The same finding can be listed twice; one rectangle per spot, or the keys collide.
        const seen = new Set();
        (coordinates || []).forEach((item) => {
            if (!item) {
                return;
            }
            // Prefer the page size the engine measured against. Its y is an offset from the
            // bottom of THAT page, so flipping it with a height the engine never saw shifts
            // the band by the difference. The rectangle is normalised by the same pair, so
            // the percentages stay right either way.
            const pageW = item.pageWidth || nativeWidth;
            const pageH = item.pageHeight || nativeHeight;
            const t = transformAnnotationCoordinates(item, pageW, pageH);
            // transformAnnotationCoordinates returns a 1-based page number.
            if (!t || t.pageIndex !== pageIndex + 1) {
                return;
            }
            const key = annotationKey(item);
            if (seen.has(key)) {
                return;
            }
            seen.add(key);
            rects.push({
                key,
                isCurrent: currentKeys.has(key),
                found: item.found,
                name: item.name,
                left: (t.x / pageW) * 100,
                top: (t.y / pageH) * 100,
                width: (t.width / pageW) * 100,
                height: (t.height / pageH) * 100,
            });
        });
        return rects;
    }, [coordinates, currentKeys]);

    const renderPage = useCallback((renderProps) => {
        const {pageIndex, scale, rotation} = renderProps;
        // width/height are the page layer's size at the current zoom; a quarter turn swaps them.
        const turned = Math.abs(rotation % 180) === 90;
        const nativeWidth = (turned ? renderProps.height : renderProps.width) / scale;
        const nativeHeight = (turned ? renderProps.width : renderProps.height) / scale;

        return (
            <>
                {renderProps.canvasLayer.children}
                {/* The text layer is what makes the page selectable and searchable. */}
                {renderProps.textLayer.children}
                {renderProps.annotationLayer.children}
                {rectsForPage(pageIndex, nativeWidth, nativeHeight).map((rect) => (
                    <div
                        key={rect.key}
                        data-annotation-key={rect.key}
                        title={rect.name || undefined}
                        className={[
                            'pdfHighlight',
                            rect.found === false ? 'pdfHighlight-missing' : 'pdfHighlight-found',
                            // "Current" is an accent on top of the colour, never instead of
                            // it - recolouring a found row red to say "you clicked this"
                            // would read as "not found".
                            rect.isCurrent ? 'pdfHighlight-current' : '',
                        ].filter(Boolean).join(' ')}
                        style={{
                            left: `${rect.left}%`,
                            top: `${rect.top}%`,
                            width: `${rect.width}%`,
                            height: `${rect.height}%`,
                        }}
                    />
                ))}
            </>
        );
    }, [rectsForPage]);

    /* -------------------------------------------------------------- *
     * Scrolling
     * -------------------------------------------------------------- */

    const cancelRetry = useCallback(() => {
        if (retryRef.current) {
            clearTimeout(retryRef.current);
            retryRef.current = null;
        }
    }, []);

    /**
     * Bring one highlight into view.
     *
     * The page it sits on may not be in the DOM yet - the viewer only renders pages near the
     * viewport - so the jump is issued first and the overlay is then looked for until it
     * appears. Bounded, and cancelled whenever another scroll supersedes it.
     */
    const scrollToKey = useCallback((pageIndex, key) => {
        cancelRetry();
        // jumpToPage is zero-based; pageIndex here is the 1-based number the transform emits.
        apiRef.current.jumpToPage(Math.max(pageIndex - 1, 0));

        let attempts = 0;
        const look = () => {
            const node = rootRef.current && key
                ? rootRef.current.querySelector(`[data-annotation-key="${CSS.escape(key)}"]`)
                : null;
            if (node) {
                node.scrollIntoView({behavior: 'smooth', block: 'center'});
                retryRef.current = null;
                return;
            }
            attempts += 1;
            if (attempts < SCROLL_RETRY_LIMIT) {
                retryRef.current = setTimeout(look, SCROLL_RETRY_MS);
            } else {
                // The page is in view even if the rectangle never materialised.
                retryRef.current = null;
            }
        };
        look();
    }, [cancelRetry]);

    /**
     * Highlight a set of annotations in the "current" colour and scroll to the first of them.
     * Exposed on the ref, and driven by the `scrollTarget` prop.
     */
    const scrollToAnnotation = useCallback((annotation, isScroll = true) => {
        if (!annotation) {
            return;
        }
        const list = Array.isArray(annotation) ? annotation.filter(Boolean) : [annotation];
        if (list.length === 0) {
            return;
        }
        if (useNativeViewer) {
            // No overlays in the browser's viewer, but it can still be sent to the right page.
            setNativePage(targetPage(list));
            return;
        }
        if (!docRef.current) {
            // Stashed rather than polled for - the document-loaded handler flushes it.
            console.warn('PdfViewer: scroll target arrived before the document; queued');
            pendingTargetRef.current = annotation;
            return;
        }

        setCurrentKeys(isScroll ? new Set(list.map(annotationKey)) : new Set());

        const first = list[0];
        // The page number comes out of the transform itself rather than being re-derived
        // here - the two must agree or the jump lands on a page with no overlay on it. The
        // canvas dimensions do not affect which page it reports, so 1x1 is enough.
        const resolved = transformAnnotationCoordinates(first, 1, 1);
        if (!resolved) {
            // A bare {pageIndex} target has no rectangle to find - just bring its page up.
            // jumpToPage is zero-based: pageIndex already is, page_number is 1-based.
            const page = typeof first.page_number === 'number' ? first.page_number - 1 : first.pageIndex;
            if (typeof page === 'number') {
                cancelRetry();
                apiRef.current.jumpToPage(Math.max(page, 0));
            }
            return;
        }
        scrollToKey(resolved.pageIndex, annotationKey(first));
    }, [scrollToKey, cancelRetry, useNativeViewer]);

    useEffect(() => {
        if (scrollTarget) {
            scrollToAnnotation(scrollTarget);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollTarget]);

    useEffect(() => cancelRetry, [cancelRetry]);

    /* -------------------------------------------------------------- *
     * Zoom and document events
     * -------------------------------------------------------------- */

    const handleDocumentLoad = useCallback((event) => {
        docRef.current = event.doc;
        setTotalPages(event.doc.numPages);
        setLoadFailed(false);
        if (pendingTargetRef.current) {
            const pending = pendingTargetRef.current;
            pendingTargetRef.current = null;
            scrollToAnnotation(pending);
        }
    }, [scrollToAnnotation]);

    const handlePageChange = useCallback((event) => {
        if (onPageChange) {
            // The library counts from zero; every caller of this component counts from one.
            onPageChange(event.currentPage + 1);
        }
    }, [onPageChange]);

    const handleZoom = useCallback((event) => {
        scaleRef.current = event.scale;
    }, []);

    const zoomIn = useCallback(() => {
        apiRef.current.zoomTo(scaleRef.current + SCALE_STEP);
    }, []);

    const zoomOut = useCallback(() => {
        apiRef.current.zoomTo(Math.max(MIN_SCALE, scaleRef.current - SCALE_STEP));
    }, []);

    const scrollToPage = useCallback((pageNum) => {
        if (useNativeViewer) {
            setNativePage(Math.max(pageNum, 1));
            return;
        }
        const clamped = Math.min(Math.max(pageNum, 1), totalPages || 1);
        apiRef.current.jumpToPage(clamped - 1);
    }, [totalPages, useNativeViewer]);

    const download = useCallback(async () => {
        if (!docRef.current) {
            return;
        }
        const data = await docRef.current.getData();
        const url = URL.createObjectURL(new Blob([data], {type: 'application/pdf'}));
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || DEFAULT_FILE_NAME;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [fileName]);

    const reset = useCallback(() => {
        cancelRetry();
        setCurrentKeys(new Set());
        apiRef.current.zoomTo(SpecialZoomLevel.PageWidth);
        apiRef.current.jumpToPage(0);
    }, [cancelRetry]);

    // A wide pane reads better at twice the fit-to-width scale; the old version kept a second
    // container at double width for this, which meant rasterising the document twice.
    useEffect(() => {
        if (!docRef.current) {
            return;
        }
        apiRef.current.zoomTo(doublePageWidth ? 2 : SpecialZoomLevel.PageWidth);
    }, [doublePageWidth, totalPages]);

    useEffect(() => {
        if (isRestPdfMode && docRef.current) {
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRestPdfMode]);

    useImperativeHandle(ref, () => ({
        scrollToAnnotation,
        scrollToPage,
        zoomIn,
        zoomOut,
        download,
        reset,
    }), [scrollToAnnotation, scrollToPage, zoomIn, zoomOut, download, reset]);

    /* -------------------------------------------------------------- *
     * Markup
     * -------------------------------------------------------------- */

    const errorState = (
        <div className="pdf-error">
            <svg width="48" height="48" viewBox="0 0 16 16" fill="#dc2626">
                <path d="M8 15A7 7 0 118 1a7 7 0 010 14zm0 1A8 8 0 108 0a8 8 0 000 16z"/>
                <path
                    d="M7.002 11a1 1 0 112 0 1 1 0 01-2 0zM7.1 4.995a.905.905 0 111.8 0l-.35 3.507a.552.552 0 01-1.1 0L7.1 4.995z"/>
            </svg>
            <h4>Unable to Access PDF</h4>
            <p>The document could not be loaded. Please try again later.</p>
        </div>
    );

    if (!source || loadFailed) {
        return errorState;
    }

    if (useNativeViewer) {
        // The presigned url exactly as received - the browser fetches it itself, no proxy.
        const src = nativePage ? `${pdfUrl}#page=${nativePage}` : pdfUrl;
        return (
            <div ref={rootRef} className="pdfPane" style={{width, height}} title={fileName}>
                <iframe title={fileName || DEFAULT_FILE_NAME} src={src} className="pdfNativeFrame"/>
            </div>
        );
    }

    return (
        <div ref={rootRef} className="pdfPane" style={{width, height}} title={fileName}>
            <Worker workerUrl={PDF_WORKER_URL}>
                <Viewer
                    // A new document is a new viewer - remounting is cheaper and more reliable
                    // than asking the library to swap the file underneath its own state.
                    key={source.fileUrl}
                    fileUrl={source.fileUrl}
                    httpHeaders={source.httpHeaders}
                    // No characterMap: the 168 .bcmap files it points at are only needed by
                    // PDFs whose fonts use a predefined CJK or legacy CMap, and claim
                    // documents are Latin-encoded. If one ever renders as blank or garbled
                    // glyphs, copy node_modules/pdfjs-dist/cmaps to public/assets/pdfjs/ and
                    // pass characterMap={{url: '/assets/pdfjs/cmaps/', isCompressed: true}}.
                    defaultScale={SpecialZoomLevel.PageWidth}
                    plugins={[
                        defaultLayoutPluginInstance,
                        pageNavigationPluginInstance,
                        zoomPluginInstance,
                    ]}
                    renderPage={renderPage}
                    renderError={() => errorState}
                    renderLoader={(percentages) => (
                        <div className="loader-gif">
                            <div className="loading-progress">
                                <div className="loading-progress-bar" style={{width: `${percentages}%`}}/>
                            </div>
                            <span>Loading PDF... {Math.round(percentages)}%</span>
                        </div>
                    )}
                    onDocumentLoad={handleDocumentLoad}
                    onPageChange={handlePageChange}
                    onZoom={handleZoom}
                />
            </Worker>
        </div>
    );
});

PdfViewer.propTypes = {
    pdfUrl: PropTypes.string.isRequired,
    fileName: PropTypes.string,
    coordinates: PropTypes.array,
    scrollTarget: PropTypes.any,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    doublePageWidth: PropTypes.bool,
    isRestPdfMode: PropTypes.bool,
    docType: PropTypes.string,
    claimNumber: PropTypes.any,
    apiBaseUrl: PropTypes.string,
    getAuthToken: PropTypes.func,
    onPageChange: PropTypes.func,
};

PdfViewer.defaultProps = {
    fileName: DEFAULT_FILE_NAME,
    coordinates: [],
    scrollTarget: null,
    width: '100%',
    height: '842px',
    doublePageWidth: false,
    isRestPdfMode: false,
    docType: '',
    claimNumber: null,
    apiBaseUrl: '',
    getAuthToken: () => localStorage.getItem('jwtToken'),
    onPageChange: null,
};

export default PdfViewer;
